import React from "react";
import { Navigate, Route, useLocation } from "react-router";
import { RedirectService } from "src/services/redirectService/redirectService";
import ClientsOverviewPage from "src/ui/containers/clients/overview/ClientsOverviewPage";
import UniversalLicensesPage from "src/ui/containers/dashboard/licenses/UniversalLicensesPage";
import ReleaseNotesDevicePage from "src/ui/containers/embedded/release-notes/ReleaseNotesDevicePage";
import ReleaseNotesPage from "src/ui/containers/embedded/release-notes/ReleaseNotesPage";
import OssLicensesPage from "src/ui/containers/legal/OssLicensesPage";
import CallbackPage from "src/ui/containers/unauthorized/components/CallbackPage";
import LoginPage from "src/ui/containers/unauthorized/components/LoginPage/LoginPage";
import LogoutPage from "src/ui/containers/unauthorized/components/LogoutPage";
import RegisterPage from "src/ui/containers/unauthorized/components/RegisterPage/RegisterPage";

import AccountApiKeyPage from "@account/components/AccountApiKeyPage/AccountApiKeyPage";
import AccountPortalSettingsPage from "@account/components/AccountPortalSettingsPage/AccountPortalSettingsPage";
import AccountProfilePage from "@account/components/AccountProfilePage/AccountProfilePage";
import AccountSecurityPage from "@account/components/AccountSecurityPage/AccountSecurityPage";
import ConfirmRegistrationPage from "@account/components/ConfirmRegistrationPage/ConfirmRegistrationPage";
import PasswordRecoveryPage from "@account/components/PasswordRecoveryPage/PasswordRecoveryPage";
import PasswordResetPage from "@account/components/PasswordResetPage";
import DevicesPage from "@dashboard/devices/DevicesPage";
import DownloadsPage from "@dashboard/downloads/DownloadsPage";
import EnrollmentPage from "@dashboard/enrollment/EnrollmentPage";
import UploadPage from "@dashboard/files/UploadPage";
import GroupsPage from "@dashboard/groups/GroupsPage";
import InvitationPage from "@dashboard/invitation/InvitationPage";
import OverviewPage from "@dashboard/overview/OverviewPage";
import PoliciesPage from "@dashboard/policies/PoliciesPage";
import ProductPage from "@dashboard/products/ProductPage";
import ProvisioningPage from "@dashboard/provisioning/ProvisioningPage";
import RepositoriesPage from "@dashboard/repositories/RepositoriesPage";
import RolloutsPage from "@dashboard/rollouts/RolloutsPage";
import WorkspaceConfigurationPage from "@dashboard/workspaces/WorkspaceConfigurationPage";
import WorkspaceMembersPage from "@dashboard/workspaces/WorkspaceMembersPage";
import WorkspacesIntroPage from "@dashboard/workspaces/WorkspacesIntroPage";
import WorkspaceSubscriptionPage from "@dashboard/workspaces/WorkspaceSubscriptionPage";
import { ApmRoutes } from "@elastic/apm-rum-react";
import { lazyChunk } from "@shared/ChunkLoadErrorBoundary";

import { PageForbidden, PageNotFound } from "./components/NavigationPages";
import { PageSuspense } from "./components/PageSuspense";
import { AuthGuard } from "./guards/AuthGuard";
import { PageGuard } from "./guards/PageGuard";
import { PrefetchGuard } from "./guards/PrefetchGuard";

const AccountBillingPage = lazyChunk(() => import("@account/components/AccountBillingPage/AccountBillingPage"));
const AdminAccountsPage = lazyChunk(() => import("@admin/components/AdminAccountsPage/AdminAccountsPage"));
const AdminAnnouncementsPage = lazyChunk(() => import("@admin/components/AdminAnnouncementsPage/AdminAnnouncementsPage"));
const AdminBuildsPage = lazyChunk(() => import("@admin/components/AdminBuildsPage/AdminBuildsPage"));
const AdminCertificatePage = lazyChunk(() => import("@admin/components/AdminCertificatePage"));
const AdminEmailPage = lazyChunk(() => import("@admin/components/AdminEmailPage/AdminEmailPage"));
const AdminEventsPage = lazyChunk(() => import("@admin/components/AdminEventsPage/AdminEventsPage"));
const AdminExpirationsPage = lazyChunk(() => import("@admin/components/AdminExpirationsPage"));
const AdminFdroidPage = lazyChunk(() => import("@admin/components/AdminFdroidPage/AdminFdroidPage"));
const AdminFilesPage = lazyChunk(() => import("@admin/components/AdminFilesPage/AdminFilesPage"));
const AdminGroupsPage = lazyChunk(() => import("@admin/components/AdminGroupsPage/AdminGroupsPage"));
const AdminLicensesPage = lazyChunk(() => import("@admin/components/AdminLicensesPage/AdminLicensesPage"));
const AdminMembersPage = lazyChunk(() => import("@admin/components/AdminMembersPage"));
const AdminClientPage = lazyChunk(() => import("@admin/components/AdminOauthClientPage/AdminOauthClientPage"));
const AdminProductsPage = lazyChunk(() => import("@admin/components/AdminProductsPage/AdminProductsPage"));
const AdminRolesPage = lazyChunk(() => import("@admin/components/AdminRolesPage/AdminRolesPage"));
const AdminWorkspacesPage = lazyChunk(() => import("@admin/components/AdminWorkspacesPage/AdminWorkspacesPage"));
const ScreenViewPage = lazyChunk(() => import("@screenView/ui/ScreenViewPage/ScreenViewPage"));

export default function Router() {
    const location = useLocation();

    React.useEffect(() => {
        // Skip scroll for embedded pages (iframe context)
        if (location.pathname.startsWith("/embedded/")) {
            return;
        }

        if (window.self === window.top) {
            window.scrollTo(0, 0);
        }
    }, [location]);

    return (
        <ApmRoutes>
            <Route path="/authorization/callback" element={<CallbackPage />} />

            <Route path="/redirect" element={<RedirectService />} />

            <Route element={<PageGuard />}>
                <Route path="/invitation" element={<InvitationPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/logout" element={<LogoutPage />} />
                <Route path="/register" element={<RegisterPage />} />

                <Route
                    path="/screenview"
                    element={
                        <PageSuspense>
                            <ScreenViewPage />
                        </PageSuspense>
                    }
                />

                <Route path="account">
                    <Route path="confirm" element={<ConfirmRegistrationPage />} />
                    <Route path="recover" element={<PasswordRecoveryPage />} />
                    <Route path="reset" element={<PasswordResetPage />} />
                </Route>

                <Route element={<AuthGuard />}>
                    <Route element={<PrefetchGuard />}>
                        <Route path="account">
                            <Route
                                path="2fa/disable"
                                element={<Navigate to="/account/settings/security?mode=2fa-disable" replace />}
                            />
                            <Route
                                path="2fa/enable"
                                element={<Navigate to="/account/settings/security?mode=2fa-enable" replace />}
                            />
                            <Route path="delete" element={<Navigate to="/account/settings/profile?mode=delete" replace />} />
                            <Route path="settings">
                                <Route index element={<AccountPortalSettingsPage />} />
                                <Route path="profile" element={<AccountProfilePage />} />
                                <Route path="security" element={<AccountSecurityPage />} />
                                <Route
                                    path="billing"
                                    element={
                                        <PageSuspense>
                                            <AccountBillingPage />
                                        </PageSuspense>
                                    }
                                />
                                <Route path="apikey" element={<AccountApiKeyPage />} />
                            </Route>
                            <Route path="workspaces">
                                <Route index element={<WorkspacesIntroPage />} />
                                <Route path="members" element={<WorkspaceMembersPage />} />
                                <Route path="subscription" element={<WorkspaceSubscriptionPage />} />
                                <Route path="configuration" element={<WorkspaceConfigurationPage />} />
                            </Route>
                        </Route>
                    </Route>

                    <Route path="admin" element={<PageSuspense />}>
                        <Route path="accounts" element={<AdminAccountsPage />} />
                        <Route path="builds" element={<AdminBuildsPage />} />
                        <Route path="certificate" element={<AdminCertificatePage />} />
                        <Route path="clients" element={<AdminClientPage />} />
                        <Route path="email" element={<AdminEmailPage />} />
                        <Route path="events" element={<AdminEventsPage />} />
                        <Route path="expirations" element={<AdminExpirationsPage />} />
                        <Route path="fdroid-repos" element={<AdminFdroidPage />} />
                        <Route path="files" element={<AdminFilesPage />} />
                        <Route path="groups" element={<AdminGroupsPage />} />
                        <Route path="licenses" element={<AdminLicensesPage />} />
                        <Route path="members" element={<AdminMembersPage />} />
                        <Route path="announcements" element={<AdminAnnouncementsPage />} />
                        <Route path="products" element={<AdminProductsPage />} />
                        <Route path="roles" element={<AdminRolesPage />} />
                        <Route path="workspaces" element={<AdminWorkspacesPage />} />
                    </Route>

                    <Route element={<PrefetchGuard />}>
                        <Route index element={<Navigate to="/dashboard/overview" replace />} />
                        <Route path="dashboard">
                            <Route index element={<Navigate to="/dashboard/overview" replace />} />
                            <Route path="devices" element={<DevicesPage />} />
                            <Route path="enrollment" element={<EnrollmentPage />} />
                            <Route path="downloads" element={<DownloadsPage />} />
                            <Route path="files" element={<UploadPage />} />
                            <Route path="groups" element={<GroupsPage />} />
                            <Route path="licenses" element={<UniversalLicensesPage />} />
                            <Route path="overview" element={<OverviewPage />} />
                            <Route path="policies" element={<PoliciesPage />} />
                            <Route path="products" element={<ProductPage />} />
                            <Route path="products/:codename/:androidVersion" element={<ProductPage />} />
                            <Route path="provisioning" element={<ProvisioningPage />} />
                            <Route path="provisioning/:provisioningId" element={<ProvisioningPage />} />
                            <Route path="repositories" element={<RepositoriesPage />} />
                            <Route path="rollouts" element={<RolloutsPage />} />
                            <Route path="workspaces" element={<Navigate to="/account/workspaces" replace />} />
                        </Route>

                        <Route path="clients">
                            <Route path="overview" element={<ClientsOverviewPage />} />
                        </Route>
                    </Route>

                    {/* Unknown paths below a protected prefix have to stay inside the auth guard, or a
                        signed-out visitor is left on a "not found" page instead of the login redirect. */}
                    <Route path="account/*" element={<PageNotFound />} />
                    <Route path="admin/*" element={<PageNotFound />} />
                    <Route path="clients/*" element={<PageNotFound />} />
                    <Route path="dashboard/*" element={<PageNotFound />} />
                </Route>

                <Route path="embedded">
                    <Route path="release-notes" element={<ReleaseNotesPage />} />
                    <Route path="release-notes/:device" element={<ReleaseNotesDevicePage />} />
                    <Route path="third-party" element={<OssLicensesPage />} />
                </Route>

                <Route path="/forbidden" element={<PageForbidden />} />
                <Route path="*" element={<PageNotFound />} />
            </Route>
        </ApmRoutes>
    );
}
